'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { cn } from '@/lib/utils';
import { Sparkles, Zap, Smile, Heart } from 'lucide-react';
import { motion } from 'framer-motion';

gsap.registerPlugin(ScrollTrigger);

const keyComponents = [
    {
        icon: Sparkles,
        titleKey: 'whatIs.keyComponents.flawlessAppearance.title',
        descriptionKey: 'whatIs.keyComponents.flawlessAppearance.description',
    },
    {
        icon: Zap,
        titleKey: 'whatIs.keyComponents.perfectAlignment.title',
        descriptionKey: 'whatIs.keyComponents.perfectAlignment.description',
    },
    {
        icon: Smile,
        titleKey: 'whatIs.keyComponents.radiantBrightness.title',
        descriptionKey: 'whatIs.keyComponents.radiantBrightness.description',
    },
    {
        icon: Heart,
        titleKey: 'whatIs.keyComponents.improvedHealth.title',
        descriptionKey: 'whatIs.keyComponents.improvedHealth.description',
    }
];

export default function WhatIsHollywoodSmileSection() {
    const { t } = useTranslation('hollywood-smile');
    const { isRTL } = useRTL();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Title animation
            gsap.fromTo(titleRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: "top 80%",
                        end: "bottom 20%",
                        once: true
                    }
                }
            );

            // Content animation
            gsap.fromTo(contentRef.current,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    delay: 0.3,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: contentRef.current,
                        start: "top 80%",
                        end: "bottom 20%",
                        once: true
                    }
                }
            );

            // Cards animation
            if (cardsRef.current?.children) {
                gsap.fromTo(cardsRef.current.children,
                    { opacity: 0, y: 50, scale: 0.9 },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.8,
                        stagger: 0.15,
                        delay: 0.6,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: cardsRef.current,
                            start: "top 80%",
                            end: "bottom 20%",
                            once: true
                        }
                    }
                );
            }

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="what-is"
            ref={sectionRef}
            className="py-20 bg-slate-50 dark:bg-navy-950 relative overflow-hidden"
        >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute top-10 left-10 w-32 h-32 bg-gold rounded-full blur-2xl"></div>
                <div className="absolute bottom-20 right-20 w-40 h-40 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-gold/20 rounded-full blur-3xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto mb-16">
                    <h2
                        ref={titleRef}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif"
                    >
                        {t('whatIs.title.part1')}{' '}
                        <span className="text-gold relative">
                            {t('whatIs.title.part2')}
                            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gold rounded-full"></div>
                        </span>
                    </h2>

                    <div ref={contentRef} className="space-y-6">
                        <p className="text-xl md:text-2xl text-slate-600 dark:text-slate-300 leading-relaxed">
                            {t('whatIs.description')}
                        </p>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                            viewport={{ once: true }}
                            className={cn(
                                "inline-flex items-center bg-gold/10 text-gold px-6 py-3 rounded-full text-lg font-medium border border-gold/20",
                                isRTL ? "space-x-reverse space-x-3" : "space-x-3"
                            )}
                        >
                            <Sparkles className="w-6 h-6" />
                            <span>{t('whatIs.ctaButton')}</span>
                            <Sparkles className="w-6 h-6" />
                        </motion.div>
                    </div>
                </div>

                {/* Key Components Grid */}
                <div ref={cardsRef} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {keyComponents.map((component, index) => (
                        <motion.div
                            key={index}
                            whileHover={{
                                scale: 1.05,
                                y: -10
                            }}
                            transition={{ duration: 0.3 }}
                            className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-200 dark:border-slate-700 group relative overflow-hidden"
                        >
                            {/* Background Gradient */}
                            <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl bg-gradient-to-br from-gold/20 to-gold/5"></div>

                            <div className="relative z-10 flex flex-col items-center text-center">
                                {/* Icon */}
                                <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 bg-gold/20 border-2 border-gold/30">
                                    <component.icon className="w-8 h-8 text-gold transition-colors duration-300" />
                                </div>

                                {/* Content */}
                                <h3 className="text-xl font-bold text-navy dark:text-white mb-4 group-hover:text-gold transition-colors duration-300">
                                    {t(component.titleKey)}
                                </h3>

                                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                                    {t(component.descriptionKey)}
                                </p>

                                {/* Decorative Element */}
                                <div className="absolute bottom-4 right-4 w-12 h-12 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-300 bg-gold/40 blur-sm"></div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
} 
